/**
 * THE HAPPINESS STORY — Core JS v2
 * No jQuery. Vanilla ES6+.
 * Handles: scroll reveal, header glass, page transitions, Lottie lazy-load
 */

(function () {
  'use strict';

  /* ── Scroll Reveal (Intersection Observer) ───────────────────── */
  var revealObserver = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('ths2-visible');
          revealObserver.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
  );

  function initReveal() {
    document.querySelectorAll('.ths2-reveal').forEach(function (el) {
      revealObserver.observe(el);
    });
  }

  /* ── Header Glass Effect ─────────────────────────────────────── */
  function initHeaderScroll() {
    var header = document.getElementById('ths2-header');
    if (!header) return;
    var scrolled = false;

    function handleScroll() {
      var isScrolled = window.scrollY > 80;
      if (isScrolled !== scrolled) {
        scrolled = isScrolled;
        header.classList.toggle('ths2-header--scrolled', scrolled);
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
  }

  /* ── Page Transition — REMOVED 2026-08-06 ─────────────────────
     THIS WAS THE LIVE WHITE-SCREEN-ON-BACK BUG. Reproduced against
     www.thehappinessstory.com in Chrome with the back/forward cache
     enabled: press Back and the homepage returns as
     `ths2-page-wrap ths2-page-exiting` — computed opacity 0,
     pointer-events none. A blank page that cannot even be tapped.

     Mechanism: this handler called preventDefault() on every
     internal link, added .ths2-page-exiting (whose animation is
     `forwards`, so it RESTS at opacity 0 / pointer-events none),
     then navigated 200ms later. The browser froze the document in
     that state for the back/forward cache. On Back the frozen DOM
     is restored exactly as it was — still faded out — and nothing
     ever removed the class, because a bfcache restore fires neither
     DOMContentLoaded nor load. The site has no unload or
     beforeunload handler, so bfcache engages readily; this hit
     every shopper who pressed Back on Chrome Android or iOS Safari.

     Three further defects went with it:
       · 200ms of dead latency added to EVERY internal navigation,
         with pointer-events:none over the page for the duration, so
         an impatient second tap did nothing.
       · No modifier-key or button check, so ctrl/cmd-click and
         middle-click were hijacked — open-in-new-tab silently
         became a same-tab navigation.
       · `animation: ths2-page-exit 0.2s var(--ths2-t-smooth)
         forwards` expands to `0.2s 0.38s cubic-bezier(...)`, so the
         token supplied a 0.38s DELAY, not the easing it looks like.

     Nothing replaces it, because the replacement already shipped:
     ths3-transitions.css does page-to-page transitions natively
     with `@view-transition { navigation: auto }`. That never
     intercepts a click, never delays a navigation, never leaves a
     class behind, and is skipped entirely on browsers without it.
     The matching CSS rule and keyframes are deleted from
     ths2-animations.css so the class is inert even if something
     ever adds it again.
  ───────────────────────────────────────────────────────────────── */

  /* Safety net for the same class of bug anywhere else: a page
     restored from the back/forward cache must never come back
     invisible. Cheap, and it runs on exactly the event a bfcache
     restore does fire. */
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    var wrap = document.querySelector('.ths2-page-wrap');
    if (wrap) {
      wrap.classList.remove('ths2-page-exiting');
      wrap.style.opacity = '';
      wrap.style.pointerEvents = '';
    }
  });

  /* ── Stat Counter Animation ──────────────────────────────────── */
  function animateCounter(el) {
    var target = parseInt(el.getAttribute('data-count'), 10);
    var duration = 1800;
    var start = null;

    function step(timestamp) {
      if (!start) start = timestamp;
      var progress = Math.min((timestamp - start) / duration, 1);
      var eased = 1 - Math.pow(1 - progress, 3); /* ease-out cubic */
      el.textContent = Math.floor(eased * target).toLocaleString('en-IN');
      if (progress < 1) requestAnimationFrame(step);
    }

    requestAnimationFrame(step);
  }

  function initCounters() {
    var counterObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            animateCounter(entry.target);
            counterObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.5 }
    );

    document.querySelectorAll('[data-count]').forEach(function (el) {
      counterObserver.observe(el);
    });
  }

  /* ── Lottie Lazy Loader ──────────────────────────────────────── */
  function initLottie() {
    var lottieEls = document.querySelectorAll('[data-lottie]');
    if (lottieEls.length === 0) return;

    var script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/lottie-web/5.12.2/lottie.min.js';
    script.onload = function () {
      lottieEls.forEach(function (el) {
        lottie.loadAnimation({
          container: el,
          renderer: 'svg',
          loop: true,
          autoplay: true,
          path: el.getAttribute('data-lottie')
        });
      });
    };
    document.head.appendChild(script);
  }

  /* ── Video autoplay on viewport enter ───────────────────────── */
  function initVideoAutoplay() {
    var videoObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var video = entry.target;
          if (entry.isIntersecting) {
            video.play().catch(function () {});
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.3 }
    );

    document.querySelectorAll('video[data-autoplay]').forEach(function (v) {
      videoObserver.observe(v);
    });
  }

  /* ── Image skeleton fade-in on load ─────────────────────────── */
  function initImageLoad() {
    document.querySelectorAll('.ths2-img-lazy').forEach(function (img) {
      if (img.complete) {
        img.classList.add('ths2-img-loaded');
      } else {
        img.addEventListener('load', function () {
          img.classList.add('ths2-img-loaded');
        });
      }
    });
  }

  /* ── Sticky header offset for anchor links ───────────────────── */
  function initAnchorOffset() {
    document.querySelectorAll('a[href^="#"]').forEach(function (a) {
      a.addEventListener('click', function (e) {
        var targetId = a.getAttribute('href').slice(1);
        var target = document.getElementById(targetId);
        if (!target) return;
        e.preventDefault();
        var headerH = parseInt(
          getComputedStyle(document.documentElement)
            .getPropertyValue('--ths2-header-h'),
          10
        ) || 72;
        var top = target.getBoundingClientRect().top + window.scrollY - headerH - 16;
        window.scrollTo({ top: top, behavior: 'smooth' });
      });
    });
  }

  /* ── Back-to-top button ──────────────────────────────────────── */
  function initBackToTop() {
    var btn = document.getElementById('ths2-back-to-top');
    if (!btn) return;

    window.addEventListener('scroll', function () {
      btn.classList.toggle('ths2-visible', window.scrollY > 600);
    }, { passive: true });

    btn.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  /* ── Dispatch custom event when cart updates (for cart drawer) ─ */
  window.ths2CartUpdate = function (cart) {
    document.dispatchEvent(new CustomEvent('ths2:cart-updated', { detail: cart }));
  };

  /* ── Init on DOM ready ───────────────────────────────────────── */
  function init() {
    initReveal();
    initHeaderScroll();
    initCounters();
    initLottie();
    initVideoAutoplay();
    initImageLoad();
    initAnchorOffset();
    initBackToTop();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
